import { useRef, useState } from "react"

export interface MediaItem {
  id: number
  url: string
  content_type: string
  media_type: string
  landscape: boolean
}

interface MediaGalleryProps {
  medias: MediaItem[]
  zoomScale?: number
}

export default function MediaGallery({ medias, zoomScale = 2.5 }: MediaGalleryProps) {
  if (medias.length === 0) return null

  return (
    <div className="grid grid-cols-2 gap-4">
      {medias.map((media) =>
        media.media_type === "video" ? (
          <VideoTile key={media.id} media={media} />
        ) : (
          <ImageTile key={media.id} media={media} zoomScale={zoomScale} />
        )
      )}
    </div>
  )
}

function ImageTile({
  media,
  zoomScale,
}: {
  media: MediaItem
  zoomScale: number
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [zoomed, setZoomed] = useState(false)
  const isMobile =
    typeof window !== "undefined" && "ontouchstart" in window

  const handleEnter = () => {
    if (isMobile || !imageRef.current) return
    setZoomed(true)
    imageRef.current.style.transform = `scale(${zoomScale})`
  }

  const handleLeave = () => {
    if (isMobile || !imageRef.current) return
    setZoomed(false)
    imageRef.current.style.transform = "scale(1)"
    imageRef.current.style.transformOrigin = "center center"
  }

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobile || !zoomed || !imageRef.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    imageRef.current.style.transformOrigin = `${x}% ${y}%`
  }

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseMove={handleMove}
      className={`overflow-hidden rounded-lg bg-gray-100 aspect-square relative group ${
        media.landscape ? "col-span-2" : ""
      }`}
      style={{ cursor: isMobile ? "default" : zoomed ? "zoom-out" : "zoom-in" }}
    >
      <img
        ref={imageRef}
        src={media.url}
        alt=""
        width={media.landscape ? 1600 : 800}
        height={800}
        className="h-full w-full object-cover object-center transition-opacity group-hover:opacity-95"
        style={{ transition: "transform-origin 0.05s ease-out" }}
      />
    </div>
  )
}

function VideoTile({ media }: { media: MediaItem }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)

  const togglePlay = () => {
    if (!videoRef.current) return
    if (videoRef.current.paused) {
      videoRef.current.play()
      setPlaying(true)
    } else {
      videoRef.current.pause()
      setPlaying(false)
    }
  }

  return (
    <button
      type="button"
      onClick={togglePlay}
      className={`overflow-hidden rounded-lg bg-gray-800 relative aspect-square cursor-pointer group ${
        media.landscape ? "col-span-2" : ""
      }`}
    >
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        className="h-full w-full object-cover object-center"
      >
        <source src={media.url} type={media.content_type} />
      </video>
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
            <i className="fa-solid fa-play text-gray-800 text-xl ml-1" />
          </div>
        </div>
      )}
    </button>
  )
}
